import {
  FaUtensils,
  FaBed,
  FaTshirt,
  FaGamepad,
  FaReceipt,
  FaBell, // <-- added
  FaStar,
  FaCoffee,
  FaClock
} from "react-icons/fa";

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Room from "../../models/Room";
import Hotel from "../../models/Hotel";
import { protectGuestPortal } from "../../utils/access";
import FullPageSpinner from "../../components/FullPageSpinner";
import { sendRequest } from "../../utils/api";
import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ConfirmModalContext";
import ExtendStayModal from "../../Modal/bookings/ExtendStayModal";
import Customer from "../../models/Customer";
import Booking from "../../models/Booking";
import BookedRoom from "../../models/BookedRoom";
import { DEFAULT_CUSTOMER_EMAIL } from "../../constants";


interface Guest {
  id: string;
  guestName: string;
  roomNo: string;
  roomId: string;
  bookingId: string;
  roomRate: number;
  email: string;
};

export default function GuestPortal() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();
  //  "⚠️ Your stay expired today. Consider extending your booking!"
  const [notifications, setNotifications] = useState(0);

  const [hotel, setHotel] = useState<Hotel>({} as Hotel);
  const [guest, setGuest] = useState<Guest>({} as Guest);
  const [room, setRoom] = useState<Room>({} as Room);
  const [booking, setBooking] = useState<Booking>({} as Booking);
  const [bookedRoom, setBookedRoom] = useState<BookedRoom>({} as BookedRoom);
  //  const [ho, setGuests] = useState<Customer[]>([] as Customer[]);

  const [loading, setLoading] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [rating, setRating] = useState(0);


  useEffect(() => {
    protectGuestPortal(navigate);

    const initData = async () => {

      try {

        setLoading(true);
        const guestStr = localStorage.getItem("user");
        const roomStr = localStorage.getItem("room");
        const hotel = localStorage.getItem("hotel");
        const bookingStr = localStorage.getItem("booking");
        const bookedRoomStr = localStorage.getItem("bookedRoom");

        let rm: Room | null = null;
        if (roomStr) rm = JSON.parse(roomStr);
        if (rm) {
          const room = await sendRequest("GET", `/rooms/${rm.id}`);
          setRoom(room);
        }

        let booking: Booking | null = null;
        if (bookingStr) booking = JSON.parse(bookingStr);

        let bookedRoom: BookedRoom | null = null;
        if (bookedRoomStr) bookedRoom = JSON.parse(bookedRoomStr);
        if (bookedRoom) setBookedRoom(bookedRoom);

        // console.log(bookedRoom)

        let guest: Customer | null = null;
        if (guestStr) guest = JSON.parse(guestStr);

        if (guest && rm && booking) setGuest({
          id: guest?.id,
          guestName: guest?.name || "Guest",
          roomId: rm.id,
          roomNo: rm.number,
          roomRate: rm.cashPrice,
          bookingId: booking?.id,
          email: guest.email ?? DEFAULT_CUSTOMER_EMAIL
        });

        if (hotel) setHotel(JSON.parse(hotel));
      } catch {

      } finally {
        setLoading(false);
      }
    }
    initData();
  }, []);

  const menuItems = [
    {
      title: "Foods",
      description: "Order delicious meals",
      icon: FaUtensils({ size: 22 }),   // fork & knife for food
      path: "/guests/menu/foods",        // unique route
    },
    {
      title: "Drinks",
      description: "Order refreshing drinks", // updated text
      icon: FaCoffee({ size: 22 }),
      path: "/guests/menu/drinks",
    },
    {
      title: "Rooms",
      description: "Explore available rooms",
      icon: FaBed({ size: 22 }),
      path: "/guests/menu/rooms",
    },
    {
      title: "Laundry",
      description: "Request laundry services",
      icon: FaTshirt({ size: 22 }),
      path: "/guests/menu/laundries",
    },
    {
      title: "Games",
      description: "Book indoor activities",
      icon: FaGamepad({ size: 22 }),
      path: "/guests/menu/games",
    },
    {
      title: "Extend Stay",
      description: "Extend your booking easily from here",
      icon: FaClock({ size: 22 }),
      path: "/guests/extend-stay",
    },
    {
      title: "My Bill",
      description: "View charges and payment history",
      icon: FaReceipt({ size: 22 }),
      path: "/guests/billing",
    },
    {
      title: "Leave a Review",
      description: "Share your experience with us",
      icon: FaStar({ size: 22 }),
      path: "/guests/review",
    }
  ];

  const resetForm = () => {
    setReviewText("");
    setRating(0);
    setShowReviewModal(false);
  }

  const createGuestReview = async () => {
    setLoading(true);
    const missingFields: string[] = [];
    if (!reviewText) missingFields.push("Guest Review");
    if (missingFields.length > 0) {
      setLoading(false);
      return openModal(
        "Missing Fields",
        `Please fill in: ${missingFields.join(", ")}`,
        closeModal, false, "Close"
      );
    }
    try {
      const data = {
        rating,
        roomId: room.id,
        review: reviewText,
        customerId: guest.id,
      };
      await sendRequest("POST", "/guests/reviews", data);
      showToast("Review submitted successfully!", { type: "success" });
    } catch (err: any) {
      if (err.status === 401) showToast("Session Timeout.", { type: "error" });
      else showToast("Unable to submit review. Try again.", { type: "error" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-10">
      {/* <ChatBubble /> */}

      <FullPageSpinner show={loading} />
      
      {showExtendModal && (
        <ExtendStayModal
          guest={{ ...guest, guestEmail: guest.email }}
          isPaidByGuest={true}
          onClose={() => setShowExtendModal(false)}
          onExtend={(newBooking: any) => {
          }}
        />
      )}

      {/* Scrolling Banner */}
      <div className="overflow-hidden relative bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800 px-4 py-2 rounded-r-xl shadow-inner">
        {room.headline && <div
          className="whitespace-nowrap inline-block"
          style={{
            display: "inline-block",
            paddingLeft: "100%",
            animation: "marquee 30s linear infinite",
          }}
        >
          {room.headline} &nbsp; — &nbsp; {room.headline} &nbsp; — &nbsp; {room.headline}
        </div>}

        <style>
          {`
      @keyframes marquee {
        0% { transform: translateX(0%); }
        100% { transform: translateX(-100%); }
      }
    `}
        </style>
      </div>
      {/* Premium Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-b-3xl shadow-lg">

        {/* Hotel Name + Notification on same line */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{hotel.name}</h1>

          <div className="relative cursor-pointer">
            {FaBell({ size: 22 })}
            {notifications > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                {notifications}
              </span>
            )}
          </div>
        </div>

        {/* Welcome & Room info below */}
        <div className="flex items-center justify-between mt-3">
          <p className="text-sm opacity-90">
            Welcome back, <span className="font-semibold capitalize">{guest.guestName ?? "Guest"}</span> 👋
          </p>

          <span className="bg-white/20 backdrop-blur px-4 py-1 rounded-full text-sm">
            Room {room.number ?? "_"}
          </span>
        </div>
      </div>

      {/* Services Section */}
      <div className="px-5 mt-8">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          Hotel Services
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {menuItems.map((item, index) => (
            <div
              key={index}
              onClick={() => {
                if (item.title.toLocaleLowerCase() === "leave a review") {
                  setShowReviewModal(true);
                } else if (item.title.toLocaleLowerCase() === "extend stay") {

                  if (bookedRoom.bookingType !== "fullTime")
                    return openModal(
                      "Extension Restricted ⚠️",
                      "Only Full-time bookings are eligible for stay extension. Kindly reach out to the receptionist for further assistance.",
                      () => { },
                      false,
                      "Close"
                    );
                  setShowExtendModal(true);
                } else {
                  navigate(item.path);
                }
              }}
              className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-blue-100 text-blue-600 p-3 rounded-xl">
                  {item.icon}
                </div>

                <h3 className="text-lg font-semibold text-gray-800">
                  {item.title}
                </h3>
              </div>

              <p className="text-sm text-gray-500">{item.description}</p>
            </div>
          ))}
        </div>



        {showReviewModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">

            <div className="bg-white rounded-2xl p-6 w-96 shadow-xl">

              <h2 className="text-xl font-semibold mb-4">
                Rate Your Stay
              </h2>

              {/* Star Rating */}
              <div className="flex gap-2 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`cursor-pointer text-2xl ${star <= rating ? "text-yellow-400" : "text-gray-300"
                      }`}
                    onClick={() => setRating(star)}
                  >
                    ★
                  </span>
                ))}
              </div>

              {/* Review Text */}
              <textarea
                placeholder="Write your review..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                className="w-full border rounded-lg p-2 mb-4"
                rows={4}
              />

              {/* Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => resetForm()}
                  className="px-4 py-2 bg-gray-200 rounded-md"
                >
                  Cancel
                </button>

                <button
                  onClick={() => {
                    resetForm();
                    createGuestReview();
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md"
                >
                  Submit
                </button>
              </div>

            </div>

          </div>
        )}

      </div>

      {/* Logout Section */}
      <div className="mt-12 pt-6 border-t flex justify-center">
        <button
          onClick={() => {
            localStorage.removeItem("authToken");
            navigate("/guests/login");
          }}
          className="text-sm text-red-500 hover:text-red-600 font-medium border border-red-200 px-6 py-2 rounded-lg hover:bg-red-50 transition"
        >
          Logout
        </button>
      </div>
    </div>
  );
}
